/**
 * Seedance2OptionsPanel - Collapsible settings panel for Seedance 2.0 video generation.
 * Shows function mode (T2V/I2V/R2V), quality, duration, resolution, aspect ratio,
 * audio toggle, and real per-second cost estimate.
 */

import { Wand2, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  SEEDANCE2_QUALITY_OPTIONS,
  SEEDANCE2_DURATIONS,
  SEEDANCE2_RESOLUTION_OPTIONS,
  SEEDANCE2_ASPECT_RATIO_OPTIONS,
  SEEDANCE2_FUNCTION_MODES,
  DEFAULT_SEEDANCE2_CONFIG,
  calculateSeedance2Tokens,
  type Seedance2Config,
} from '@/constants/seedance2Video';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';

interface Seedance2OptionsPanelProps {
  seedance2Config: Seedance2Config;
  /** Upstream USD cost of one clip, or `null` when the configuration has no price. */
  estimatedCost: number | null;
  durationMinutes: number;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  onChange: (config: Seedance2Config) => void;
  disabled: boolean;
  /** Hide cost estimate (e.g. when parent already shows unified cost) */
  showCost?: boolean;
}

const FUNCTION_MODE_LABEL_KEYS: Record<string, { i18nKey: string; fallback: string }> = {
  [SEEDANCE2_FUNCTION_MODES.TEXT_TO_VIDEO]: {
    i18nKey: 'videoSource.seedance2TextToVideo',
    fallback: 'Text to Video',
  },
  [SEEDANCE2_FUNCTION_MODES.IMAGE_TO_VIDEO]: {
    i18nKey: 'videoSource.seedance2ImageToVideo',
    fallback: 'Image to Video',
  },
  [SEEDANCE2_FUNCTION_MODES.REFERENCE_TO_VIDEO]: {
    i18nKey: 'videoSource.seedance2ReferenceToVideo',
    fallback: 'References to Video',
  },
};

export function Seedance2OptionsPanel({
  seedance2Config,
  estimatedCost: _estimatedCost,
  durationMinutes: _durationMinutes,
  showAdvanced,
  onToggleAdvanced,
  onChange,
  disabled,
  showCost = true,
}: Seedance2OptionsPanelProps) {
  const { t } = useTranslation();

  const tokenCost = calculateSeedance2Tokens(
    seedance2Config.duration ?? DEFAULT_SEEDANCE2_CONFIG.duration,
    seedance2Config.quality ?? DEFAULT_SEEDANCE2_CONFIG.quality
  );

  const functionModeOptions: SegmentOption[] = (
    Object.values(SEEDANCE2_FUNCTION_MODES) as string[]
  ).map((mode) => {
    const labelInfo = FUNCTION_MODE_LABEL_KEYS[mode];
    return {
      value: mode,
      label: t(labelInfo.i18nKey, { defaultValue: labelInfo.fallback }),
    };
  });

  const qualityOptions: SegmentOption[] = SEEDANCE2_QUALITY_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
  }));

  const durationOptions: SegmentOption[] = SEEDANCE2_DURATIONS.map((d) => ({
    value: d,
    label: `${d}s`,
  }));

  const resolutionOptions: SegmentOption[] = SEEDANCE2_RESOLUTION_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const aspectRatioOptions: SegmentOption[] = SEEDANCE2_ASPECT_RATIO_OPTIONS.map((option) => ({
    value: option.value,
    label: option.value,
    description: option.label.split(' ')[0],
  }));

  const currentFunctionMode =
    seedance2Config.function_mode ?? DEFAULT_SEEDANCE2_CONFIG.function_mode;
  const currentQuality = seedance2Config.quality ?? DEFAULT_SEEDANCE2_CONFIG.quality;
  const currentDuration = seedance2Config.duration ?? DEFAULT_SEEDANCE2_CONFIG.duration;
  const currentResolution = seedance2Config.resolution ?? DEFAULT_SEEDANCE2_CONFIG.resolution;
  const currentAspectRatio = seedance2Config.aspect_ratio ?? DEFAULT_SEEDANCE2_CONFIG.aspect_ratio;

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-black/20">
      <button
        type="button"
        onClick={onToggleAdvanced}
        className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-white/80 flex items-center space-x-2">
          <Wand2 className="h-4 w-4 text-primary-400" />
          <span>
            {t('videoSource.seedance2Settings', { defaultValue: 'Seedance 2.0 Settings' })}
          </span>
        </span>
        {showAdvanced ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>

      {showAdvanced && (
        <div className="p-4 space-y-4 border-t border-white/5">
          {/* Function Mode Selection */}
          <SegmentedPicker
            label={t('videoSource.seedance2FunctionMode', { defaultValue: 'Function Mode' })}
            options={functionModeOptions}
            value={currentFunctionMode}
            onChange={(val) =>
              !disabled &&
              onChange({
                ...seedance2Config,
                function_mode: val as Seedance2Config['function_mode'],
              })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Quality Selection */}
          <SegmentedPicker
            label={t('videoSource.seedance2Quality', { defaultValue: 'Quality' })}
            options={qualityOptions}
            value={currentQuality}
            onChange={(val) =>
              !disabled &&
              onChange({ ...seedance2Config, quality: val as Seedance2Config['quality'] })
            }
            size="md"
            layout="equal"
            disabled={disabled}
          />

          {/* Duration Selection */}
          <SegmentedPicker
            label={t('videoSource.seedance2Duration', { defaultValue: 'Clip Duration' })}
            options={durationOptions}
            value={currentDuration}
            onChange={(val) =>
              !disabled &&
              onChange({ ...seedance2Config, duration: val as Seedance2Config['duration'] })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Resolution Selection */}
          <SegmentedPicker
            label={t('videoSource.seedance2Resolution', { defaultValue: 'Resolution' })}
            options={resolutionOptions}
            value={currentResolution}
            onChange={(val) =>
              !disabled &&
              onChange({ ...seedance2Config, resolution: val as Seedance2Config['resolution'] })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Aspect Ratio Selection */}
          <SegmentedPicker
            label={t('videoSource.seedance2AspectRatio', { defaultValue: 'Aspect Ratio' })}
            options={aspectRatioOptions}
            value={currentAspectRatio}
            onChange={(val) =>
              !disabled &&
              onChange({
                ...seedance2Config,
                aspect_ratio: val as Seedance2Config['aspect_ratio'],
              })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Audio Toggle */}
          <div className="flex items-center justify-between">
            <label className="label-inline">
              {t('videoSource.seedance2Audio', { defaultValue: 'Generate Audio (free)' })}
            </label>
            <button
              type="button"
              onClick={() => {
                if (!disabled) {
                  onChange({
                    ...seedance2Config,
                    generate_audio: !seedance2Config.generate_audio,
                  });
                }
              }}
              disabled={disabled}
              className={[
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                seedance2Config.generate_audio ? 'bg-primary-500' : 'bg-white/10',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ]
                .filter(Boolean)
                .join(' ')}
              role="switch"
              aria-checked={seedance2Config.generate_audio}
            >
              <span
                className={[
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  seedance2Config.generate_audio ? 'translate-x-6' : 'translate-x-1',
                ].join(' ')}
              />
            </button>
          </div>

          {/* Cost Estimate */}
          {showCost && (
            <div className="mt-4 text-right">
              <div className="text-xs text-white/40">
                {t('videoSource.estimatedCost', { defaultValue: 'Cost' })}
              </div>
              <div className="text-lg font-semibold text-yellow-400">
                {tokenCost} {t('common.tokens', { defaultValue: 'tokens' })}
              </div>
              <div className="text-xs text-white/40 mt-0.5">
                {seedance2Config.duration}s &middot;{' '}
                {seedance2Config.quality === 'fast' ? '$0.2419/s' : '$0.3034/s'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
